<script setup>
import { useId } from 'vue';

// shadcn-style form field: label above the control, optional description
// below it; the control's own validation message (Vuetify) shows underneath.
// The slot receives `id` to put on the control so the label is linked:
//   <UiFormField label="Email" v-slot="{ id }">
//     <v-text-field :id="id" v-model="form.email" :rules="[required]" />
//   </UiFormField>
defineProps({
  label: { type: String, required: true },
  description: { type: String, default: '' },
  required: { type: Boolean, default: false },
});

const id = useId();
</script>

<template>
  <div class="ui-field">
    <div class="ui-field__label-row">
      <label :for="id" class="ui-field__label">
        {{ label }}
        <span v-if="required" class="ui-field__required" aria-hidden="true">*</span>
      </label>
      <!-- e.g. a "Forgot password?" link -->
      <slot name="label-end" />
    </div>
    <slot :id="id" />
    <p v-if="description" class="ui-field__description">{{ description }}</p>
  </div>
</template>

<style scoped>
.ui-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}
.ui-field__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.ui-field__label {
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  color: var(--foreground);
}
.ui-field__required {
  color: var(--muted-foreground);
  margin-left: 2px;
}
.ui-field__description {
  margin: 0;
  font-size: 13px;
  color: var(--muted-foreground);
}

/* shadcn Input: 36px, 8px radius, light border, faint shadow, focus ring */
.ui-field :deep(.v-input) {
  --v-input-control-height: 36px;
  --v-field-input-padding-top: 6px;
  --v-field-input-padding-bottom: 6px;
  --v-field-padding-start: 12px;
  --v-field-padding-end: 12px;
}
.ui-field :deep(.v-field) {
  font-size: 14px;
}
.ui-field :deep(.v-input__details) {
  min-height: 0;
  padding-inline: 0;
  padding-top: 6px;
}
.ui-field :deep(.v-input__details:empty),
.ui-field :deep(.v-input__details .v-messages:empty) {
  padding-top: 0;
}
</style>
